import React from "react";
import { useQueryClient } from "react-query";
import {
  PaginatedListOfVesselRouterListDto,
  useAISModemApi,
  VesselRouterCreateDto,
  VesselRouterEditDto,
} from "~/api/GeneratedApi";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

export const aisRouterListQueryKey = "ais-routers" as const;

type EditArgs = {
  id: string;
  dto: VesselRouterEditDto;
};

type UseAISRoutersArgs = {
  onMutationSuccess?: () => void;
};
export function useAISRouters({ onMutationSuccess }: UseAISRoutersArgs = {}) {
  const { listModems, removeModem, editModem, createModem } = useAISModemApi();
  const qc = useQueryClient();

  const handleMutationUpdate = React.useCallback(() => {
    onMutationSuccess?.();
    void qc.invalidateQueries(aisRouterListQueryKey as string);
  }, [onMutationSuccess, qc]);

  const listQuery = useCustomQuery(aisRouterListQueryKey, () => listModems(), {
    // refetchInterval: 10000,
    select: React.useCallback(
      (d: PaginatedListOfVesselRouterListDto) => d.results,
      [],
    ),
  });

  const getRouterById = React.useCallback(
    (id: string) => {
      const routerItem = listQuery.data?.find((x) => x.deviceId === id);
      return routerItem ?? null;
    },
    [listQuery.data],
  );

  const alarmDeviceIds = React.useMemo(() => {
    const alarmIds: string[] = [];

    (listQuery.data ?? []).forEach((item) => {
      if ((item.vesselTelemetry?.activeAlarms?.length ?? 0) > 0) {
        alarmIds.push(item.deviceId);
      }
    });
    return alarmIds;
  }, [listQuery.data]);

  const deviceHasAlarms = React.useCallback(
    (id: string | undefined) => {
      if (!id) return false;
      return alarmDeviceIds.includes(id);
    },
    [alarmDeviceIds],
  );

  const someRouterHasAlarm = React.useMemo(
    () => alarmDeviceIds.length > 0,
    [alarmDeviceIds],
  );

  const removeMutation = useCustomMutation((id: string) => removeModem(id), {
    onSuccess: handleMutationUpdate,
  });
  const editMutation = useCustomMutation(
    ({ dto, id }: EditArgs) => editModem(dto, id),
    { onSuccess: handleMutationUpdate },
  );
  const addMutation = useCustomMutation(
    (dto: VesselRouterCreateDto) => createModem(dto),
    { onSuccess: handleMutationUpdate },
  );

  return {
    listQuery,
    getRouterById,
    removeMutation,
    editMutation,
    addMutation,
    listModems,
    deviceHasAlarms,

    someRouterHasAlarm,
  };
}
